import { cn } from "@/engine/support/classNames";

/**
 * The light a glade's ground is lit in: the fire's, or a team's own colour —
 * `--team-1` for your own side, `--team-2` for the other (#758).
 */
export type GladeLight = "fire" | "team-1" | "team-2";

const GLOW: Record<GladeLight, string> = {
  fire: "bg-[radial-gradient(ellipse_at_50%_100%,hsl(var(--primary)/0.28),hsl(var(--primary)/0.06)_45%,transparent_70%)]",
  "team-1":
    "bg-[radial-gradient(ellipse_at_50%_100%,hsl(var(--team-1)/0.22),hsl(var(--team-1)/0.05)_45%,transparent_70%)]",
  "team-2":
    "bg-[radial-gradient(ellipse_at_50%_100%,hsl(var(--team-2)/0.22),hsl(var(--team-2)/0.05)_45%,transparent_70%)]",
};

/**
 * Pines along the horizon, as one path in a 0–100 box: heights and spacing
 * fixed rather than random, so the treeline is the same on every render and
 * on the server.
 */
const PINES = [
  [2, 52],
  [7, 70],
  [11, 46],
  [17, 62],
  [24, 40],
  [31, 58],
  [36, 74],
  [43, 48],
  [57, 50],
  [63, 72],
  [69, 44],
  [75, 60],
  [82, 38],
  [88, 66],
  [94, 50],
  [99, 58],
] as const;

const treeline = (pines: readonly (readonly [number, number])[]) =>
  `M0 100 ${pines
    .map(([x, height]) => `L${x - 3} 100 L${x} ${100 - height} L${x + 3} 100`)
    .join(" ")} L100 100 Z`;

const NEAR = treeline(PINES);
/** The same line shifted and taller, paler behind the near one: a farther row. */
const FAR = treeline(
  PINES.map(([x, height]) => [(x + 4) % 100, Math.min(95, height * 1.3)]),
);

/** Where the few stars are, in percent of the sky; fixed for the same reason. */
const STARS = [
  [8, 18],
  [21, 34],
  [33, 12],
  [47, 28],
  [58, 9],
  [71, 31],
  [84, 15],
  [93, 38],
] as const;

/**
 * What a figure stands in, so it stands somewhere: a sky, a treeline on the
 * horizon and ground lit from below (syncani#897, syncani#898). A team band
 * draws one behind all its figures and a plain room's cell one behind each.
 * The horizon sits a little above the middle, which is where a centred
 * figure's feet fall. Drawn only from tokens, so every palette brings its own
 * night, and static, so a room that draws no scene draws none for it.
 */
export function Glade({ light }: { light: GladeLight }) {
  return (
    <div
      aria-hidden="true"
      data-testid="glade"
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
    >
      <div className="absolute inset-x-0 top-0 h-[55%] bg-[linear-gradient(to_bottom,hsl(var(--background)),hsl(var(--panel)))]" />
      {STARS.map(([x, y]) => (
        <span
          key={x}
          className="absolute size-0.5 rounded-full bg-foreground/50"
          style={{ left: `${x}%`, top: `${y}%` }}
        />
      ))}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-x-0 top-[33%] h-[22%] w-full"
      >
        <path d={FAR} className="fill-raise/45" />
        <path d={NEAR} className="fill-raise" />
      </svg>
      <div className="absolute inset-x-0 bottom-0 top-[55%] bg-[linear-gradient(to_bottom,hsl(var(--raise)),hsl(var(--panel))_35%,hsl(var(--background)))]" />
      <div
        className={cn("absolute inset-x-0 bottom-0 top-[40%]", GLOW[light])}
      />
    </div>
  );
}
